import type { Interval } from 'luxon'
import type { FilterInterval } from '~/models/Time'
import { DateTime } from 'luxon'
import useArrayUtils from '~/utils/arrayUtils'
import { FilterIntervalName } from '~/models/Time'
import { useSchoolYear } from '~/composables/useSchoolYear'

export function useTime() {
  const { currentSchoolYearInterval } = useSchoolYear()
  const { unique, truthy } = useArrayUtils()

  const currentWeekNumber: number = DateTime.local().weekNumber

  const getWeekNumbersInInterval = (interval: Interval): number[] => {
    const days = interval.splitBy({ day: 1 })
    if (days.length === 0)
      return []
    return days
      .map(({ start: day }) => day?.weekNumber ?? 0)
      .filter(truthy<number>)
      .filter(unique)
  }

  const getWeekNumbersInCurrentSchoolYear = (): number[] => {
    return getWeekNumbersInInterval(currentSchoolYearInterval)
  }

  const weekNumberToDateTime = (weekNumber: number, year?: number): DateTime => {
    return DateTime.fromObject({
      weekYear: year ?? DateTime.local().weekYear,
      weekNumber,
      weekday: 1,
      second: 1,
    })
  }

  const weekToDateTime = (week: { weekNumber: number, weekYear: number }): DateTime => {
    return DateTime.fromObject({
      weekNumber: week.weekNumber,
      weekYear: week.weekYear,
    })
  }

  const isoStringToDateTime = (isoString: string): DateTime => {
    return DateTime.fromISO(isoString)
  }

  const dateTimeToStandardOutputFormat = (dateTime: DateTime): string => {
    return dateTime.toFormat('dd.MM.yyyy')
  }

  const dateTimeToAlternativeOutputFormat = (dateTime: DateTime): string => {
    return dateTime.toFormat('dd LLL yyyy')
  }

  const formatReportTimestamp = (timestamp: string | null | undefined): string => {
    if (!timestamp) {
      return ''
    }

    try {
      const dateTime = DateTime.fromISO(timestamp)
      if (!dateTime.isValid) {
        return ''
      }
      return dateTime.toFormat('dd.MM.yy')
    } catch {
      return ''
    }
  }

  const currentWeekDate = weekToDateTime({
    weekNumber: currentWeekNumber,
    weekYear: DateTime.local().weekYear,
  })

  const currentDate = DateTime.local().startOf('day').toFormat('YYYY-MM-DD')

  const timestampInFuture = (timestamp: number): boolean => {
    const timestampDate = DateTime.fromSeconds(timestamp).endOf('day')
    return timestampDate > DateTime.local()
  }

  const isoStringInFuture = (isoString: string): boolean => {
    const dateTime: DateTime = DateTime.fromISO(isoString)
    return dateTime > DateTime.local()
  }

  const schoolStart = currentSchoolYearInterval.start
  if (!schoolStart) {
    throw new Error('Invalid school year start')
  }
  const intervalFloor = schoolStart.startOf('day')

  const clampToSchoolStart = (dateTime: DateTime): DateTime =>
    dateTime.toMillis() < intervalFloor.toMillis() ? intervalFloor : dateTime

  const todayEnd = DateTime.local().endOf('day')

  const reportFilterIntervals: FilterInterval[] = [
    {
      name: FilterIntervalName.OneWeek,
      start: clampToSchoolStart(DateTime.local().minus({ weeks: 1 }).startOf('day')),
      end: todayEnd,
    },
    {
      name: FilterIntervalName.ThreeMonths,
      start: clampToSchoolStart(DateTime.local().minus({ months: 3 }).startOf('day')),
      end: todayEnd,
    },
    {
      name: FilterIntervalName.SixMonths,
      start: clampToSchoolStart(DateTime.local().minus({ months: 6 }).startOf('day')),
      end: todayEnd,
    },
    {
      name: FilterIntervalName.SinceStart,
      start: intervalFloor,
      end: todayEnd,
    },
  ]

  const filteredReportFilterIntervals: FilterInterval[] = reportFilterIntervals.filter((interval) => {
    if (interval.name === FilterIntervalName.OneWeek) {
      return DateTime.local().minus({ weeks: 1 }).startOf('day') >= intervalFloor
    }
    if (interval.name === FilterIntervalName.ThreeMonths) {
      return DateTime.local().minus({ months: 3 }).startOf('day') >= intervalFloor
    }
    if (interval.name === FilterIntervalName.SixMonths) {
      return DateTime.local().minus({ months: 6 }).startOf('day') >= intervalFloor
    }
    if (interval.name === FilterIntervalName.SinceStart) {
      return true
    }
    return true
  })

  const getDateRange = (period: string = 'today'): { from: string, to: string } => {
    const now = DateTime.local()
    const today = now.startOf('day')

    switch (period) {
      case 'today': {
        return {
          from: today.toFormat('yyyy-MM-dd\'T\'HH:mm:ss.SSS'),
          to: today.plus({ days: 1 }).toFormat('yyyy-MM-dd\'T\'HH:mm:ss.SSS')
        }
      }

      case 'last7': {
        return {
          from: today.minus({ days: 6 }).toFormat('yyyy-MM-dd\'T\'HH:mm:ss.SSS'),
          to: today.plus({ days: 1 }).toFormat('yyyy-MM-dd\'T\'HH:mm:ss.SSS')
        }
      }

      case 'last30': {
        return {
          from: today.minus({ days: 29 }).toFormat('yyyy-MM-dd\'T\'HH:mm:ss.SSS'),
          to: today.plus({ days: 1 }).toFormat('yyyy-MM-dd\'T\'HH:mm:ss.SSS')
        }
      }

      case 'spring': {
        const schoolYearStart = currentSchoolYearInterval.start!
        const schoolYearEnd = currentSchoolYearInterval.end!
        const springStart = DateTime.local(schoolYearStart.year + 1, 1, 1)
        return {
          from: springStart.toFormat('yyyy-MM-dd\'T\'HH:mm:ss.SSS'),
          to: schoolYearEnd.toFormat('yyyy-MM-dd\'T\'HH:mm:ss.SSS')
        }
      }

      case 'autumn': {
        const schoolYearStart = currentSchoolYearInterval.start!
        const autumnEnd = DateTime.local(schoolYearStart.year, 12, 31, 23, 59, 59)
        return {
          from: schoolYearStart.toFormat('yyyy-MM-dd\'T\'HH:mm:ss.SSS'),
          to: autumnEnd.toFormat('yyyy-MM-dd\'T\'HH:mm:ss.SSS')
        }
      }

      case 'schoolYear': {
        return {
          from: currentSchoolYearInterval.start!.toFormat('yyyy-MM-dd\'T\'HH:mm:ss.SSS'),
          to: currentSchoolYearInterval.end!.toFormat('yyyy-MM-dd\'T\'HH:mm:ss.SSS')
        }
      }

      default: {
        return {
          from: today.toFormat('yyyy-MM-dd\'T\'HH:mm:ss.SSS'),
          to: today.plus({ days: 1 }).toFormat('yyyy-MM-dd\'T\'HH:mm:ss.SSS')
        }
      }
    }
  }

  return {
    isoStringInFuture,
    timestampInFuture,
    currentWeekNumber,
    currentWeekDate,
    currentDate,
    getWeekNumbersInInterval,
    getWeekNumbersInCurrentSchoolYear,
    weekNumberToDateTime,
    isoStringToDateTime,
    dateTimeToStandardOutputFormat,
    dateTimeToAlternativeOutputFormat,
    formatReportTimestamp,
    weekToDateTime,
    filteredReportFilterIntervals,
    getDateRange,
  }
}

export default useTime
